import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import MetaTags from '../../components/SEO/MetaTags';
import { pageConfigs } from '../../utils/seo';

const WhyNotPTFirst: React.FC = () => {
  return (
    <>
      <MetaTags
        title={pageConfigs.blogWhyNotPTFirst.title}
        description={pageConfigs.blogWhyNotPTFirst.description}
        keywords={pageConfigs.blogWhyNotPTFirst.keywords}
        canonical={pageConfigs.blogWhyNotPTFirst.canonical}
      />
      
      <div className="bg-white">
        {/* Hero section with title */}
        <div className="bg-darkGreen text-white pt-36 pb-8 mb-16 relative overflow-hidden">
          <div className="relative z-10">
            <div className="max-w-3xl mx-auto text-center px-4">
              <h4 className="text-emerald-400 font-medium mb-2">Blog Post</h4>
              <h1 className="text-4xl lg:text-5xl font-bold mb-4 font-productSans">
                Why Not PT First? Rethinking MSK care at the front door
              </h1>
              <p className="text-emerald-100 text-lg">
                Exploring the expanding role of physical therapists as first-contact providers
              </p>
              <p className="text-emerald-200 text-sm mt-4">
                By Christina Rama PT, DPT • August 15, 2025
              </p>
            </div>
          </div>
        </div>

        {/* Blog content */}
        <div className="max-w-3xl mx-auto px-4 pb-16">
          {/* Back button at top */}
          <div className="mb-8">
            <Link
              to="/blogs"
              className="inline-flex items-center px-6 py-3 border border-gray-800 rounded-full text-gray-800 hover:bg-darkGreen hover:text-white transition-colors"
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to Blogs
            </Link>
          </div>

          {/* Featured image */}
          <div className="mb-8">
            <img 
              src="/blog/blog-6.png" 
              alt="Physical therapist as first contact provider" 
              className="w-full rounded-lg shadow-md"
            />
          </div>

          {/* Article content */}
          <article className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-6">
              There's been a growing conversation in the physical therapy world—on social media, in academic circles, and even within health policy—about a bold idea:
            </p>

            <p className="text-gray-700 leading-relaxed mb-6 font-semibold">
              Should physical therapists serve as the first point of contact for musculoskeletal (MSK) care?
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              Depending on where you practice, that might already feel like your day-to-day. In other places, it might feel far off. But overall, the role of physical therapy seems to be expanding. The question is: how far should it shift—and what would need to change for us to get there?
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">What's Already True</h2>

            <p className="text-gray-700 leading-relaxed mb-6">
              In many settings—military, sports medicine, rural care, and direct-access states—PTs already function as first-contact providers for MSK issues.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              Patients come directly to us for pain, movement limitations, or injury recovery—often without seeing a physician first.
            </p>

            <p className="text-gray-700 leading-relaxed mb-4">
              And the outcomes speak for themselves:
            </p>

            <ul className="list-disc ml-6 mb-6 text-gray-700">
              <li className="mb-2">Earlier intervention</li>
              <li className="mb-2">Lower overall costs</li>
              <li className="mb-2">Fewer unnecessary tests and medications</li>
              <li className="mb-2">Higher patient satisfaction</li>
            </ul>

            <p className="text-gray-700 leading-relaxed mb-6">
              Meanwhile, patients are actively seeking faster answers. With primary care wait times growing and orthopedic consults often delayed for weeks, PTs are stepping into that gap—not just by choice, but by necessity.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Where the Gaps Still Are</h2>

            <p className="text-gray-700 leading-relaxed mb-4">
              The opportunity is clear, but so are the barriers:
            </p>

            <ul className="list-disc ml-6 mb-6 text-gray-700">
              <li className="mb-2">Documentation tools often aren't built for clinical screening or triage</li>
              <li className="mb-2">Billing models still assume PT is downstream from a physician</li>
              <li className="mb-2">Time and space don't always allow for comprehensive evaluations or red flag screening</li>
              <li className="mb-2">Culturally, the system isn't yet designed to support PTs as autonomous decision-makers</li>
            </ul>

            <p className="text-gray-700 leading-relaxed mb-6">
              That said, documentation is finally starting to evolve.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              New tools—some powered by AI—are helping PTs document more efficiently without compromising on clinical accuracy or detail.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              That shift matters. Because when note-taking takes less time and mental bandwidth, there's more room for what really counts: critical thinking, patient care, and clinical judgment.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">If We Want the Role, We Need the Support</h2>

            <p className="text-gray-700 leading-relaxed mb-4">
              If PTs are going to serve as first-contact providers for MSK care, we need systems that reflect that responsibility. That means:
            </p>

            <ul className="list-disc ml-6 mb-6 text-gray-700">
              <li className="mb-2">Workflows that support clinical reasoning</li>
              <li className="mb-2">Seamless communication with other providers</li>
              <li className="mb-2">Education and mentorship to prepare clinicians</li>
              <li className="mb-2">Policy and reimbursement models that align with what PTs are trained to do</li>
            </ul>

            <p className="text-gray-700 leading-relaxed mb-6">
              Accountable Care Organizations and value-based models are already shifting priorities—looking for high-impact, cost-effective providers who can begin care early and reduce downstream expenses. PTs are well-positioned to meet that need. The question is whether the system will adapt in time.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              And today's PTs—whether newly licensed or experienced—are increasingly trained and prepared to take on more clinical responsibility. They're licensed, confident in differential diagnosis, and ready to lead care.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              What's often missing isn't capability—it's support and permission.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">It All Comes Back to the Patient</h2>

            <p className="text-gray-700 leading-relaxed mb-6">
              At the end of the day, this shift isn't just about professional autonomy. It's about delivering better patient care.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              In many cases, the most thorough, empathetic, and effective MSK care patients receive starts with a physical therapist. For some, we already are their first point of contact—just without the title, tools, or system behind us.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">What's Your Take?</h2>

            <p className="text-gray-700 leading-relaxed mb-4">
              Should PTs take on a larger role as first-contact providers in MSK care?
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              What would help—or hurt—that shift?
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              We'd love to hear your perspective.
            </p>
          </article>

          {/* CTA Section */}
          <div className="mt-16 p-8 bg-gradient-to-r from-emerald-50 to-blue-50 rounded-lg border border-emerald-100">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Ready to Transform Your Clinical Documentation?
              </h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Discover how Notation can help you focus more on patient care and less on paperwork.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="https://notation.fownd.care/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-emerald-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors inline-flex items-center justify-center"
                >
                  Try Notation Free
                </a>
                <Link
                  to="/demo"
                  className="bg-white text-emerald-600 px-8 py-3 rounded-lg font-semibold border-2 border-emerald-600 hover:bg-emerald-50 transition-colors inline-flex items-center justify-center"
                >
                  Watch Demo
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default WhyNotPTFirst;